import { useSubAccountsFilter } from '@src/hooks/filters/useSubAccountsFilter';
import { isExcludedByResolvedUsers, useResolvedUsers, useUsersFilter } from '@src/hooks/filters/useUsersFilter';
import { useUser } from '@src/hooks/useUser';
import type { OTCDealData } from '@src/types/OTC';
import { RepoDirectionEnum } from '@src/types/OTC/otcSharedTypes';
import { getOtcDealDisplayUser } from '@src/utils/OTC/otcDealDisplayUtils';
import {
  DealStatusEnum,
  EMPTY_OBJECT,
  filterExistsAndExcludes,
  IconName,
  ProductTypeEnum,
  SubProductTypeEnum,
  useGenericFilter,
  type BlotterTableClientLocalFilter,
  type BlotterTableFiltersProps,
  type DateRangeFilter,
  type FilterableProperty,
  type UseFilterBuilderProps,
  type UsePersistedBlotterTable,
} from '@talos/kyoko';
import { values } from 'lodash-es';
import { useCallback, useMemo, type SetStateAction } from 'react';

export interface RepoTableFilter extends DateRangeFilter {
  Directions?: RepoDirectionEnum[];
  ProductTypes?: ProductTypeEnum[];
  SubProductTypes?: SubProductTypeEnum[];
  _statuses?: DealStatusEnum[];
  Statuses?: DealStatusEnum[];
  Users?: string[];
  SubAccounts?: string[];
}

interface UseRepoFilterParams {
  persistedBlotterTable: UsePersistedBlotterTable;
}

export type UseRepoFilterOutput<TData extends OTCDealData> = {
  initialFilter: RepoTableFilter | undefined;
  filter: RepoTableFilter;
  clientSideFilter: BlotterTableClientLocalFilter<TData>;
  changeFilter: (action: SetStateAction<RepoTableFilter>) => void;
  filterableProperties: FilterableProperty[];
  filterBuilderProps: UseFilterBuilderProps;
  blotterTableFilterProps: Partial<BlotterTableFiltersProps>;
};

export function useRepoFilter<TData extends OTCDealData>({
  persistedBlotterTable,
}: UseRepoFilterParams): UseRepoFilterOutput<TData> {
  const { Name: currentUserName } = useUser();
  const usersFilter = useUsersFilter();
  const subAccountsFilter = useSubAccountsFilter({});

  const filterableProperties: FilterableProperty[] = useMemo(
    () => [
      {
        key: 'Directions',
        label: 'Direction',
        icon: IconName.ArrowUpDown,
        options: values(RepoDirectionEnum),
        getOptionLabel: (option: string) => option,
      },
      {
        key: 'Statuses',
        label: 'Status',
        icon: IconName.CheckCircle,
        options: values(DealStatusEnum),
        getOptionLabel: (option: string) => option,
      },
      subAccountsFilter,
      usersFilter,
    ],
    [subAccountsFilter, usersFilter]
  );

  const { filter, changeFilter, filterBuilderProps, blotterTableFilterProps } = useGenericFilter<RepoTableFilter>(
    filterableProperties,
    {
      initialFilter: persistedBlotterTable.initialState?.filter as RepoTableFilter | undefined,
      onFilterChanged: persistedBlotterTable.onFilterChanged,
    }
  );

  const resolvedUsers = useResolvedUsers(filter.Users, currentUserName);

  const clientSideFilter = useCallback<BlotterTableClientLocalFilter<TData>>(
    row => {
      const data = row.data;
      if (!data) {
        return false;
      }
      if (repoDirectionFilterExcludes(filter, data as RepoDirectionData)) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'SubAccounts', data, 'SubAccount')) {
        return false;
      }
      if (isExcludedByResolvedUsers(resolvedUsers, getOtcDealDisplayUser(data))) {
        return false;
      }
      return true;
    },
    [filter, resolvedUsers]
  );

  return {
    initialFilter: persistedBlotterTable.initialState?.filter as RepoTableFilter | undefined,
    filter,
    clientSideFilter,
    changeFilter,
    filterableProperties,
    filterBuilderProps,
    blotterTableFilterProps,
  };
}

const colIDToFilterBuilderKeyMap = {
  'DealInfo.Direction': 'Directions',
  Status: 'Statuses',
  user: 'Users',
  SubAccount: 'SubAccounts',
} as const;

export function colIDToFilterBuilderKey(id: string): keyof RepoTableFilter | undefined {
  return colIDToFilterBuilderKeyMap[id as keyof typeof colIDToFilterBuilderKeyMap];
}

type RepoDirectionData = {
  DealInfo: {
    Direction: RepoDirectionEnum;
  };
};

export function repoDirectionFilterExcludes(filter: RepoTableFilter, data: RepoDirectionData): boolean {
  return !!filter.Directions?.length && !filter.Directions.includes(data.DealInfo.Direction);
}

export function onlyServerFilterKeys(filter?: RepoTableFilter) {
  if (!filter) {
    return EMPTY_OBJECT;
  }

  const serverFilter: RepoTableFilter = {
    StartDate: filter.StartDate,
    EndDate: filter.EndDate,
    ProductTypes: [ProductTypeEnum.OTC],
    SubProductTypes: [SubProductTypeEnum.OTCRepo],
    Users: filter.Users,
    SubAccounts: filter.SubAccounts,
  };

  if (filter._statuses) {
    serverFilter.Statuses = filter._statuses;
  } else if (filter.Statuses) {
    serverFilter.Statuses = filter.Statuses;
  }

  return serverFilter;
}
